/**
 * Hospital CFO portal hooks (HON-006, REV-047, AI-005).
 *
 * Wraps the Portals BFF's client-facing CFO surface:
 *   - GET  /api/v1/portals/cfo-dashboard → CfoDashboard (KPIs, AR aging, embed)
 *   - POST /api/v1/portals/ask-pfa       → AskPfaAnswer (natural-language Q&A)
 *
 * Both tolerate `{ items }` / bare-object / superset envelopes and degrade on
 * 404 (or any error) to a deterministic seed so the executive dashboard always
 * renders the full design — the demo never shows a blank CFO portal. The real
 * shapes (services/portals/schemas.py `CfoDashboard`) carry `case_counts`,
 * `ar_aging`, `kpis` (a free-form scalar map) and `embedded_report`; we read
 * those defensively and fall back per-field.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

/** A headline KPI tile rendered on the CFO portal. */
export interface CfoKpi {
  key: string;
  label: string;
  /** Pre-formatted display string (the hook normalizes raw scalars). */
  display: string;
  /** number | currency | percent — drives the icon/format hint. */
  format: 'number' | 'currency' | 'percent';
  /** Signed period-over-period delta as a fraction, if known. */
  delta?: number | null;
}

/** One point on the simple revenue trend chart. */
export interface TrendPoint {
  label: string;
  value: number;
}

/** An AR-aging bucket for the CFO portal. */
export interface CfoArBucket {
  label: string;
  balance: number;
  invoice_count: number;
}

/** A program-health indicator (RAG-style). */
export interface ProgramHealthIndicator {
  label: string;
  /** healthy | watch | at_risk (normalized from on_track | watch | at_risk). */
  status: 'healthy' | 'watch' | 'at_risk';
  detail: string;
}

/** Mock embedded Power BI report metadata. */
export interface CfoEmbeddedReport {
  report_name: string;
  embed_url: string;
  rls_tenant_id: string;
  is_mock: boolean;
}

/** The composed CFO dashboard the portal renders. */
export interface CfoDashboardView {
  generated_at: string;
  revenue_captured: number;
  ar_outstanding: number;
  conversion_rate: number;
  kpis: CfoKpi[];
  trend: TrendPoint[];
  ar_buckets: CfoArBucket[];
  program_health: ProgramHealthIndicator[];
  embedded_report: CfoEmbeddedReport;
}

/** A supporting figure backing an Ask PFA answer (`supporting[{label,value}]`). */
export interface AskPfaFigure {
  label: string;
  value: string;
}

/** An account drill-down row backing an Ask PFA answer (`drill_down[{account,value}]`). */
export interface AskPfaAccount {
  account: string;
  value: string;
}

/** The advisory answer to an Ask PFA natural-language question (AI-005). */
export interface AskPfaAnswer {
  question: string;
  answer: string;
  confidence: number;
  /** Disclosure / HITL label from the gateway. */
  disclosure: string;
  hitl: boolean;
  figures: AskPfaFigure[];
  accounts: AskPfaAccount[];
}

// ---------------------------------------------------------------------------
// Seed (used on 404 / error so the executive view is never blank)
// ---------------------------------------------------------------------------

const SEED_DASHBOARD: CfoDashboardView = {
  generated_at: new Date().toISOString(),
  revenue_captured: 1284500,
  ar_outstanding: 612340,
  conversion_rate: 0.64,
  kpis: [
    { key: 'revenue_captured', label: 'Revenue captured (MTD)', display: '$1,284,500', format: 'currency', delta: 0.11 },
    { key: 'ar_outstanding', label: 'AR outstanding', display: '$612,340', format: 'currency', delta: -0.04 },
    { key: 'conversion_rate', label: 'Enrollment conversion', display: '64%', format: 'percent', delta: 0.03 },
    { key: 'active_cases', label: 'Active cases', display: '1,842', format: 'number', delta: 0.06 },
    { key: 'avg_days_to_cash', label: 'Avg days to cash', display: '41', format: 'number', delta: -0.07 },
    { key: 'net_collection', label: 'Net collection rate', display: '96%', format: 'percent', delta: 0.02 },
  ],
  trend: [
    { label: 'Jan', value: 842000 },
    { label: 'Feb', value: 905000 },
    { label: 'Mar', value: 988000 },
    { label: 'Apr', value: 1043000 },
    { label: 'May', value: 1172000 },
    { label: 'Jun', value: 1284500 },
  ],
  ar_buckets: [
    { label: 'Current', balance: 318200, invoice_count: 121 },
    { label: '1-30', balance: 154800, invoice_count: 52 },
    { label: '31-60', balance: 81640, invoice_count: 24 },
    { label: '61-90', balance: 39200, invoice_count: 11 },
    { label: '90+', balance: 18500, invoice_count: 6 },
  ],
  program_health: [
    { label: 'Enrollment velocity', status: 'healthy', detail: 'Conversion up 3 pts vs prior month; pipeline ahead of target.' },
    { label: 'AR aging', status: 'watch', detail: '$57.7K is 60+ days; recommend escalation on the 90+ bucket.' },
    { label: 'SLA compliance', status: 'healthy', detail: '93% of cases within SLA across all queues.' },
    { label: 'Census throughput', status: 'at_risk', detail: 'Integration connector degraded — last census file delayed 6h.' },
  ],
  embedded_report: {
    report_name: 'CFO Revenue & Coverage Dashboard',
    embed_url: 'https://app.powerbi.com/mock/cfo-dashboard',
    rls_tenant_id: 'hosp-001',
    is_mock: true,
  },
};

function fmtCurrency(n: number): string {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function toNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v.replace(/[$,%\s]/g, ''));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * The raw CfoDashboard wire shape (services/portals/schemas.py `CfoDashboard`).
 *
 * Carries flat headline fields (`revenue_captured`, `ar_outstanding`,
 * `conversion_rate` — a 0–100 percentage), KPI tiles `kpis: [{label,value,trend}]`,
 * a monthly `trend_series: [{month,captured}]`, `program_health: [{label,status}]`,
 * `powerbi_embed_url`, plus the nested `ar_aging` / `embedded_report` objects.
 */
interface RawCfoDashboard {
  generated_at?: string;
  hospital?: string;
  revenue_captured?: number;
  ar_outstanding?: number;
  conversion_rate?: number;
  kpis?: { label?: string; value?: unknown; trend?: string }[];
  trend_series?: { month?: string; captured?: number }[];
  program_health?: { label?: string; status?: string }[];
  powerbi_embed_url?: string;
  is_mock?: boolean;
  ar_aging?: {
    total_outstanding?: number;
    open_invoice_count?: number;
    buckets?: { label?: string; balance?: number; invoice_count?: number }[];
  };
  embedded_report?: {
    report_name?: string;
    embed_url?: string;
    rls_tenant_id?: string;
    is_mock?: boolean;
  };
}

function normalizeStatus(s: string | undefined): ProgramHealthIndicator['status'] {
  const v = (s ?? '').toLowerCase();
  if (v === 'at_risk' || v === 'danger' || v === 'critical') return 'at_risk';
  if (v === 'watch' || v === 'warning' || v === 'degraded') return 'watch';
  return 'healthy';
}

/** Trend delta hint from a KPI's categorical trend (up|down|flat). */
function deltaForTrend(trend: string | undefined): number | null {
  const t = (trend ?? '').toLowerCase();
  if (t === 'up') return 0.05;
  if (t === 'down') return -0.05;
  return null;
}

/** Guess a display format for a KPI from its label + value. */
function guessFormat(label: string, value: unknown): CfoKpi['format'] {
  const l = label.toLowerCase();
  if (/rate|conversion|%|percent|compliance/.test(l)) return 'percent';
  if (/revenue|ar|cash|\$|amount|captured|collection/.test(l)) return 'currency';
  if (typeof value === 'string' && value.trim().endsWith('%')) return 'percent';
  if (typeof value === 'string' && value.trim().startsWith('$')) return 'currency';
  return 'number';
}

/** Render a KPI value for display, honoring an already-formatted string. */
function displayKpi(value: unknown, format: CfoKpi['format']): string {
  if (typeof value === 'string') return value; // backend already formatted it
  const n = toNumber(value);
  if (n === null) return String(value ?? '—');
  if (format === 'currency') return fmtCurrency(n);
  // conversion_rate-style percentages arrive 0–100; treat <=1 as a fraction.
  if (format === 'percent') return n <= 1 ? `${Math.round(n * 100)}%` : `${Math.round(n)}%`;
  return n.toLocaleString();
}

/** Normalize a 0–100 OR 0–1 conversion value to a 0–1 fraction. */
function asFraction(v: number): number {
  return v > 1 ? v / 100 : v;
}

/** Map the raw backend dashboard onto the view shape, falling back per-field. */
function adaptDashboard(raw: RawCfoDashboard | null | undefined): CfoDashboardView {
  if (!raw || typeof raw !== 'object') return SEED_DASHBOARD;

  const revenue = toNumber(raw.revenue_captured) ?? SEED_DASHBOARD.revenue_captured;
  const arOut = toNumber(raw.ar_outstanding) ?? toNumber(raw.ar_aging?.total_outstanding) ?? SEED_DASHBOARD.ar_outstanding;
  const conversion = raw.conversion_rate != null ? asFraction(raw.conversion_rate) : SEED_DASHBOARD.conversion_rate;

  const kpis: CfoKpi[] =
    raw.kpis && raw.kpis.length
      ? raw.kpis.map((k, i) => {
          const label = k.label ?? `KPI ${i + 1}`;
          const format = guessFormat(label, k.value);
          return { key: `${label}-${i}`, label, format, display: displayKpi(k.value, format), delta: deltaForTrend(k.trend) };
        })
      : SEED_DASHBOARD.kpis;

  const arBuckets =
    raw.ar_aging?.buckets && raw.ar_aging.buckets.length
      ? raw.ar_aging.buckets.map((b) => ({ label: b.label ?? '—', balance: b.balance ?? 0, invoice_count: b.invoice_count ?? 0 }))
      : SEED_DASHBOARD.ar_buckets;

  const trend =
    raw.trend_series && raw.trend_series.length
      ? raw.trend_series.map((t) => ({ label: t.month ?? '', value: t.captured ?? 0 }))
      : SEED_DASHBOARD.trend;

  // program_health from the backend has no detail text; synthesize a short one.
  const programHealth =
    raw.program_health && raw.program_health.length
      ? raw.program_health.map((p) => {
          const status = normalizeStatus(p.status);
          return {
            label: p.label ?? '—',
            status,
            detail:
              status === 'at_risk'
                ? 'Flagged for attention — review recommended.'
                : status === 'watch'
                  ? 'Trending toward threshold — monitor.'
                  : 'On track against target.',
          };
        })
      : SEED_DASHBOARD.program_health;

  return {
    generated_at: raw.generated_at ?? new Date().toISOString(),
    revenue_captured: revenue,
    ar_outstanding: arOut,
    conversion_rate: conversion,
    kpis,
    trend,
    ar_buckets: arBuckets,
    program_health: programHealth,
    embedded_report: {
      report_name: raw.embedded_report?.report_name ?? SEED_DASHBOARD.embedded_report.report_name,
      embed_url: raw.powerbi_embed_url ?? raw.embedded_report?.embed_url ?? SEED_DASHBOARD.embedded_report.embed_url,
      rls_tenant_id: raw.embedded_report?.rls_tenant_id ?? SEED_DASHBOARD.embedded_report.rls_tenant_id,
      is_mock: raw.is_mock ?? raw.embedded_report?.is_mock ?? true,
    },
  };
}

interface UseCfoDashboardResult {
  dashboard: CfoDashboardView | null;
  /** True when the data is live; false when we are showing the seed. */
  available: boolean;
  isLoading: boolean;
  error: ApiError | null;
  refresh: () => void;
}

/** Loads the tenant-scoped CFO dashboard, degrading to a seed on 404/error. */
export function useCfoDashboard(): UseCfoDashboardResult {
  const [dashboard, setDashboard] = useState<CfoDashboardView | null>(null);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);
    apiClient
      .get<RawCfoDashboard>('/portals/cfo-dashboard')
      .then((data) => {
        setDashboard(adaptDashboard(data));
        setAvailable(true);
        setError(null);
      })
      .catch((err: unknown) => {
        // Degrade gracefully: show the seed, flag as not-live. Only surface a
        // hard error for non-404s so the page still renders the design.
        setDashboard(SEED_DASHBOARD);
        setAvailable(false);
        if (err instanceof ApiError && err.status !== 404) {
          setError(err);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { dashboard, available, isLoading, error, refresh };
}

const DEFAULT_DISCLOSURE = 'AI-generated insight — advisory only. Verify before acting.';

/** Build a deterministic advisory answer for a question (client-side fallback). */
function seedAnswer(question: string): AskPfaAnswer {
  return {
    question,
    answer:
      'Based on the latest tenant-scoped figures, revenue captured month-to-date is $1.28M (up 11%), ' +
      'with $612K in outstanding AR. The largest improvement lever is the 60+ day AR bucket ($57.7K across ' +
      '17 accounts); resolving these would lift the net collection rate ~2 points. Enrollment conversion is ' +
      'holding at 64%. This is advisory decision-support — please confirm before acting.',
    confidence: 0.72,
    disclosure: DEFAULT_DISCLOSURE,
    hitl: false,
    figures: [
      { label: 'Revenue captured (MTD)', value: '$1,284,500' },
      { label: 'AR 60+ days', value: '$57,700' },
      { label: 'Conversion', value: '64%' },
      { label: 'Net collection rate', value: '96%' },
    ],
    accounts: [
      { account: 'PFA-1042 · R. Alvarez', value: '$14,200' },
      { account: 'PFA-0997 · M. Chen', value: '$11,850' },
      { account: 'PFA-1130 · D. Okafor', value: '$9,600' },
      { account: 'PFA-1077 · S. Patel', value: '$8,300' },
    ],
  };
}

/**
 * Raw Ask PFA wire shape (services/portals/schemas.py `AskPfaResponse`):
 * `{ answer, supporting: [{label,value}], drill_down: [{account,value}],
 *    confidence, disclosure, hitl }`.
 */
interface RawAskPfaAnswer {
  answer?: string;
  confidence?: number;
  disclosure?: string;
  hitl?: boolean;
  supporting?: { label?: string; value?: unknown }[];
  drill_down?: { account?: string; value?: unknown }[];
}

function adaptAnswer(question: string, raw: RawAskPfaAnswer | null | undefined): AskPfaAnswer {
  if (!raw || typeof raw !== 'object' || !raw.answer) return seedAnswer(question);
  return {
    question,
    answer: raw.answer,
    confidence: typeof raw.confidence === 'number' ? raw.confidence : 0.7,
    disclosure: raw.disclosure ?? DEFAULT_DISCLOSURE,
    hitl: raw.hitl ?? false,
    figures: (raw.supporting ?? []).map((f) => ({ label: f.label ?? '—', value: String(f.value ?? '') })),
    accounts: (raw.drill_down ?? []).map((a) => ({ account: a.account ?? '—', value: String(a.value ?? '') })),
  };
}

interface UseAskPfaResult {
  answer: AskPfaAnswer | null;
  isAsking: boolean;
  /** True when the last answer came from the live backend. */
  live: boolean;
  error: ApiError | null;
  ask: (question: string) => Promise<void>;
  reset: () => void;
}

/** Ask PFA / Performance Insights — natural-language Q&A (AI-005). */
export function useAskPfa(): UseAskPfaResult {
  const [answer, setAnswer] = useState<AskPfaAnswer | null>(null);
  const [isAsking, setIsAsking] = useState(false);
  const [live, setLive] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const ask = useCallback(async (question: string) => {
    const q = question.trim();
    if (!q) return;
    setIsAsking(true);
    setError(null);
    try {
      const data = await apiClient.post<RawAskPfaAnswer>('/portals/ask-pfa', { question: q });
      setAnswer(adaptAnswer(q, data));
      setLive(true);
    } catch (err: unknown) {
      // Degrade: show a deterministic advisory answer. Only surface non-404s.
      setAnswer(seedAnswer(q));
      setLive(false);
      if (err instanceof ApiError && err.status !== 404) {
        setError(err);
      }
    } finally {
      setIsAsking(false);
    }
  }, []);

  const reset = useCallback(() => {
    setAnswer(null);
    setError(null);
    setLive(false);
  }, []);

  return { answer, isAsking, live, error, ask, reset };
}
